import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/apiConfig';
import { socket } from '../config/socket';
import './Pedidos.css';

const PedidosDirectosListaComponent = () => {
  const navigate = useNavigate();
  const { auth } = useAuth();

  // Constante para la mesa de pedidos directos
  const MESA_DIRECTA_ID = 910100;

  // Estados de datos
  const [pedidosDirectos, setPedidosDirectos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros en memoria (operan sobre el array ya cargado)
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroPiso, setFiltroPiso] = useState('todos');
  const [pisoAsignadoDB, setPisoAsignadoDB] = useState(null);
  const [pisosDisponibles, setPisosDisponibles] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  // Modo de visualización: 'activos' (default) | 'busqueda' (historial por fechas)
  const [modoVista, setModoVista] = useState('activos');

  // Rango de fechas para el modo historial
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Paginación 
  const LIMIT = 250;
  const [offset, setOffset] = useState(0);
  const [hayMas, setHayMas] = useState(false);

  // ── Fetch con lógica dual ──────────────────────────────────────────────────
  const buildUrl = useCallback((currentOffset) => {
    const base = getApiUrl(`/pedidos/mesa/${MESA_DIRECTA_ID}`);
    if (modoVista === 'activos') {
      // Por defecto: excluir estados finalizados para mostrar solo pedidos activos
      return `${base}?excluir_estados=entregado_pagado,cancelado&limit=${LIMIT}&offset=${currentOffset}`;
    } else {
      // Modo historial: traer todos los pedidos del rango de fechas
      return `${base}?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}&limit=${LIMIT}&offset=${currentOffset}`;
    }
  }, [modoVista, fechaInicio, fechaFin]);

  const fetchPedidosDirectos = useCallback(async (isSilent = false) => {
    try {
      if (offset === 0 && !isSilent) setLoading(true);
      const response = await fetch(buildUrl(offset), {
        headers: {
          'Authorization': `Bearer ${auth?.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar pedidos directos');
      }

      const data = await response.json();
      if (offset === 0) {
        setPedidosDirectos(data);
      } else {
        setPedidosDirectos(prev => [...prev, ...data]);
      }
      
      setHayMas(data.length === LIMIT);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [auth?.token, buildUrl, offset]);

  // Carga inicial y control de estado
  useEffect(() => {
    fetchPedidosDirectos();
  }, [fetchPedidosDirectos]);

  // Fetch real-time de asignaciones para evitar JWT desactualizado (especialmente en Cajeros)
  useEffect(() => {
    if (!auth?.token || !['MOZO', 'CAJERO'].includes(auth?.user?.rol)) return;
    fetch(getApiUrl('/usuarios-admin/mis-asignaciones'), {
      headers: { 'Authorization': `Bearer ${auth.token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && data.piso_asignado !== null && data.piso_asignado !== undefined) {
          const p = Number(data.piso_asignado);
          setPisoAsignadoDB(p);
          setFiltroPiso(String(p));
        }
      })
      .catch(() => {});
  }, [auth?.token, auth?.user?.rol]);

  // Fallback: detección automática del piso asignado desde el perfil si no cargó desde DB
  useEffect(() => {
    if (pisoAsignadoDB !== null && pisoAsignadoDB !== undefined) return;
    const pisoUsuario = auth?.user?.piso_asignado;
    if (pisoUsuario !== null && pisoUsuario !== undefined && pisoUsuario !== -1) {
      setFiltroPiso(String(pisoUsuario));
    }
  }, [pisoAsignadoDB, auth?.user?.piso_asignado]);

  // Carga dinámica de pisos reales desde el catálogo de mesas
  useEffect(() => {
    if (!auth?.token) return;
    fetch(getApiUrl('/mesas'), {
      headers: { 'Authorization': `Bearer ${auth.token}` }
    })
      .then(res => res.ok ? res.json() : [])
      .then(mesas => {
        const pisos = [...new Set(mesas.map(m => Number(m.piso)).filter(p => p !== null && p !== undefined && p > 0))].sort((a, b) => a - b);
        setPisosDisponibles(pisos);
      })
      .catch(() => {});
  }, [auth?.token]);

  // Suscripción a Sockets para tiempo real
  useEffect(() => {
    if (modoVista === 'activos' && offset === 0) {
      const handleActualizacion = () => {
        fetchPedidosDirectos(true);
      };

      socket.on('pedidoCreado', handleActualizacion);
      socket.on('mesaActualizada', handleActualizacion);
      socket.on('cambioEstadoComanda', handleActualizacion);

      return () => {
        socket.off('pedidoCreado', handleActualizacion);
        socket.off('mesaActualizada', handleActualizacion);
        socket.off('cambioEstadoComanda', handleActualizacion);
      };
    }
  }, [modoVista, offset, fetchPedidosDirectos]);

  // ── Funciones de modo ─────────────────────────────────────────────────────
  const buscarPorFechas = () => {
    if (!fechaInicio || !fechaFin) return;
    setOffset(0);
    setModoVista('busqueda'); // dispara el useEffect
  };

  const volverAActivos = () => {
    setOffset(0);
    setModoVista('activos');
    setFechaInicio('');
    setFechaFin('');
    // El useEffect con [modoVista] volverá a cargar los pedidos activos y reactivará el intervalo
  };
  
  const cargarMas = () => {
    setOffset(prev => prev + LIMIT);
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatearTiempo = (timestamp) => {
    const fecha = new Date(timestamp);
    return fecha.toLocaleString();
  };

  const calcularTiempoTranscurrido = (timestamp) => {
    const fecha = new Date(timestamp);
    const ahora = new Date();
    const diferencia = Math.floor((ahora - fecha) / 1000 / 60); // en minutos

    if (diferencia < 60) {
      return `${diferencia} min`;
    } else {
      const horas = Math.floor(diferencia / 60);
      const minutos = diferencia % 60;
      return `${horas}h ${minutos}m`;
    }
  };

  const getEstadoClass = (estado) => {
    switch (estado) {
      case 'pendiente_pagado': return 'estado-pendiente';
      case 'en_prep_pagado':  return 'estado-preparacion';
      case 'listo_pagado':    return 'estado-listo';
      case 'entregado_pagado': return 'estado-entregado';
      default: return '';
    }
  };

  const tieneProdutosListos = (pedido) => {
    if (pedido.estado === 'listo_pagado') return true;
    if (pedido.detalles && pedido.detalles.length > 0) {
      return pedido.detalles.some(detalle => detalle.estado === 'listo_pagado');
    }
    return false;
  };

  const verDetallePedido = (pedidoId) => {
    navigate(`/pedidos/${pedidoId}`);
  };

  const crearPedidoDirecto = () => {
    navigate('/pedido-directo');
  };

  // ── Filtrado en memoria ───────────────────────────────────────────────────
  const filtrarPedidos = () => {
    let pedidosFiltrados = pedidosDirectos;

    // Filtrar por estado (solo aplicable cuando el selector lo permite)
    if (filtroEstado !== 'todos') {
      pedidosFiltrados = pedidosFiltrados.filter(pedido => pedido.estado === filtroEstado);
    }

    // Filtrar por piso
    if (filtroPiso !== 'todos') {
      pedidosFiltrados = pedidosFiltrados.filter(pedido => {
        const pedidoPiso = pedido.usuario_piso_asignado !== null && pedido.usuario_piso_asignado !== undefined
          ? String(pedido.usuario_piso_asignado)
          : '0';
        return pedidoPiso === filtroPiso;
      });
    }

    // Filtrar por búsqueda (número de pedido)
    if (busqueda.trim() !== '') {
      pedidosFiltrados = pedidosFiltrados.filter(pedido =>
        pedido.id.toString().includes(busqueda.trim())
      );
    }

    return pedidosFiltrados;
  };

  const handleBusquedaChange = (e) => {
    setBusqueda(e.target.value);
  };

  const limpiarBusqueda = () => {
    setBusqueda('');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="error-container">
        <div className="error">Error: {error}</div>
        <button
          onClick={() => { setError(null); }}
          className="btn-volver"
        >
          Intentar nuevamente
        </button>
      </div>
    );
  }

  const pedidosFiltrados = filtrarPedidos();

  return (
    <div className="pedidos-container">
      <div className="pedidos-header">
        <h2>Pedidos Directos</h2>
        <div className="header-actions">
          <button
            className="btn-nuevo"
            onClick={crearPedidoDirecto}
          >
            Crear Pedido Directo
          </button>
        </div>
      </div>

      {/* ── Panel de filtros ─────────────────────────────────────────────── */}
      <div className="filtros-container">
        {/* Filtro por estado */}
        <div className="filtro-estado">
          <label>Filtrar por estado:</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="todos">Todos los estados</option>
            <option value="pendiente_pagado">Pendiente</option>
            <option value="en_prep_pagado">En preparación</option>
            <option value="listo_pagado">Listo</option>
            {modoVista === 'busqueda' && (
              <option value="entregado_pagado">Entregado</option>
            )}
            {modoVista === 'busqueda' && (
              <option value="cancelado">Cancelado</option>
            )}
          </select>
        </div>

        {/* Filtro por piso (Detección inteligente y control restringido) */}
        <div className="filtro-estado">
          <label>Filtrar por piso:</label>
          {(() => {
            const hasDbRestriction = pisoAsignadoDB !== null && pisoAsignadoDB !== undefined && pisoAsignadoDB !== -1;
            const hasTokenRestriction = auth?.user?.piso_asignado !== null && auth?.user?.piso_asignado !== undefined && auth?.user?.piso_asignado !== -1;
            const isRestricted = hasDbRestriction || hasTokenRestriction;
            const activePiso = hasDbRestriction ? pisoAsignadoDB : auth?.user?.piso_asignado;

            return (
              <select
                value={filtroPiso}
                onChange={(e) => setFiltroPiso(e.target.value)}
                disabled={isRestricted}
              >
                {isRestricted ? (
                  <option value={String(activePiso)}>Piso {activePiso}</option>
                ) : (
                  <>
                    <option value="todos">Todos los pisos</option>
                    <option value="0">Piso 0 (Pedidos Directos)</option>
                    {pisosDisponibles.map(p => (
                      <option key={p} value={String(p)}>Piso {p}</option>
                    ))}
                  </>
                )}
              </select>
            );
          })()}
        </div>

        {/* Buscador por número de pedido */}
        <div className="buscador-container">
          <label>Buscar por número de pedido:</label>
          <div className="input-busqueda">
            <input
              type="text"
              placeholder="Ingrese número de pedido"
              value={busqueda}
              onChange={handleBusquedaChange}
            />
            {busqueda && (
              <button className="btn-limpiar" onClick={limpiarBusqueda}>
                ×
              </button>
            )}
          </div>
        </div>

        {/* Selector de rango de fechas para historial */}
        <div className="filtro-fechas">
          <label>Historial por fecha:</label>
          <div className="rango-fechas">
            <input
              type="date"
              value={fechaInicio}
              onChange={(e) => setFechaInicio(e.target.value)}
              max={fechaFin || undefined}
            />
            <span className="separador-fechas">→</span>
            <input
              type="date"
              value={fechaFin}
              onChange={(e) => setFechaFin(e.target.value)}
              min={fechaInicio || undefined}
            />
            {modoVista === 'busqueda' ? (
              <button className="btn-volver-activos" onClick={volverAActivos}>
                ✕ Ver activos
              </button>
            ) : (
              <button
                className="btn-buscar-fechas"
                onClick={buscarPorFechas}
                disabled={!fechaInicio || !fechaFin}
              >
                Buscar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Badge modo historial ─────────────────────────────────────────── */}
      {modoVista === 'busqueda' && (
        <div className="badge-modo-busqueda">
          📅 Mostrando historial: <strong>{fechaInicio}</strong> → <strong>{fechaFin}</strong>
          &nbsp;—&nbsp;<strong>{pedidosFiltrados.length} pedido{pedidosFiltrados.length !== 1 ? 's' : ''}</strong>
          <span className="sin-refresco">(refresco automático pausado)</span>
        </div>
      )}

      {/* ── Indicador de carga ───────────────────────────────────────────── */}
      {loading && pedidosDirectos.length === 0 && (
        <div className="loading">Cargando pedidos directos...</div>
      )}

      {/* ── Lista de pedidos ─────────────────────────────────────────────── */}
      {!loading && pedidosFiltrados.length === 0 ? (
        <div className="sin-pedidos">
          <p>
            No hay pedidos directos
            {modoVista === 'busqueda'
              ? ` entre el ${fechaInicio} y el ${fechaFin}`
              : ' activos'}
            {filtroEstado !== 'todos' ? ` en estado "${filtroEstado.replace(/_/g, ' ')}"` : ''}
            {busqueda.trim() !== '' ? ` con número "${busqueda}"` : ''}
          </p>
        </div>
      ) : (
        <div className="lista-pedidos">
          {pedidosFiltrados.map(pedido => (
            <div
              key={pedido.id}
              className={`pedido-card ${getEstadoClass(pedido.estado)} ${tieneProdutosListos(pedido) ? 'producto-listo parpadeo' : ''}`}
              onClick={() => verDetallePedido(pedido.id)}
            >
              <div className="pedido-header">
                <div className="pedido-id">Pedido #{pedido.id}</div>
                <div className="pedido-estado">{pedido.estado.replace(/_/g, ' ')}</div>
                {tieneProdutosListos(pedido) && (
                  <div className="indicador-productos-listos">
                    ¡Productos listos!
                  </div>
                )}
              </div>
              <div className="pedido-tiempo">
                <span>Inicio: {formatearTiempo(pedido.tiempo_inicio)}</span>
                <span>Tiempo: {calcularTiempoTranscurrido(pedido.tiempo_inicio)}</span>
              </div>
              <div className="pedido-productos">
                {pedido.detalles ? (
                  (() => {
                    // Filtrar productos cancelados
                    const detallesActivos = pedido.detalles.filter(d => d.estado !== 'cancelado');

                    // Agrupar productos similares
                    const productosAgrupados = {};
                    detallesActivos.forEach(detalle => {
                      const clave = `${detalle.id_producto}-${detalle.nombre_producto}`;
                      if (!productosAgrupados[clave]) {
                        productosAgrupados[clave] = {
                          ...detalle,
                          cantidad_total: detalle.cantidad,
                          subtotal: detalle.cantidad * detalle.precio_unitario
                        };
                      } else {
                        productosAgrupados[clave].cantidad_total += detalle.cantidad;
                        productosAgrupados[clave].subtotal += detalle.cantidad * detalle.precio_unitario;
                      }
                    });

                    if (Object.keys(productosAgrupados).length === 0) {
                      return <div className="sin-productos">No hay productos activos en este pedido</div>;
                    }

                    return Object.values(productosAgrupados).map((producto, index) => (
                      <div key={index} className="pedido-producto-fila">
                        <span className="pp-nombre">
                          {producto.nombre_producto}
                          {producto.estado === 'listo_pagado' && (
                            <span className="producto-estado-listo"> ✓</span>
                          )}
                        </span>
                        <span className="pp-cantidad">×{producto.cantidad_total}</span>
                        <span className="pp-subtotal">S/. {producto.subtotal.toFixed(2)}</span>
                      </div>
                    ));
                  })()
                ) : 'Detalles no disponibles'}
              </div>
              <div className="pedido-total">
                <span>Total: S/. {pedido.total}</span>
              </div>
            </div>
          ))}
          {hayMas && (
            <div className="cargar-mas-container" style={{ textAlign: 'center', margin: '20px 0', width: '100%' }}>
              <button 
                className="btn-cargar-mas" 
                onClick={cargarMas} 
                style={{ padding: '10px 20px', borderRadius: '5px', backgroundColor: '#e2e8f0', color: '#334155', border: 'none', cursor: 'pointer', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
              >
                Cargar más pedidos históricos ↓
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PedidosDirectosListaComponent;
